/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { LINK_TARGET } from '@veeam/components';

import { TextDividerPatterns, TextDividerSymbols, TextDividerUniqueSymbols } from './enums';

import type { TextConfig, TextDivider, TextParserParams, ParsedTextConfig } from './interfaces';

const textDividers: TextDivider[] = [
    {
        pattern: TextDividerPatterns.DoubleN,
        symbol: TextDividerSymbols.BlankLine,
        uniqueSymbol: TextDividerUniqueSymbols.DoubleN,
    },
    {
        pattern: TextDividerPatterns.RN,
        symbol: TextDividerSymbols.Paragraph,
        uniqueSymbol: TextDividerUniqueSymbols.SingleN,
    },
    {
        pattern: TextDividerPatterns.SingleN,
        symbol: TextDividerSymbols.Paragraph,
        uniqueSymbol: TextDividerUniqueSymbols.SingleN,
    },
    {
        pattern: TextDividerPatterns.Br,
        symbol: TextDividerSymbols.Paragraph,
        uniqueSymbol: TextDividerUniqueSymbols.Br,
    },
];

const splitTextByDividers = (text: string): string[] => {
    const repeatNum = 10;
    let dividerStr;
    let currentIndex = 0;
    let modifiedText = text.replace(new RegExp('\\t', 'g'), '');
    const wrapUniqueSymbols = (text: string) => `!${text}!`;
    const unwrapUniqueSymbols = (text: string) => text.slice(1, -1);
    const re = new RegExp(wrapUniqueSymbols(`[${textDividers.map(div => `\\${div.uniqueSymbol}`).join('|')}]{${repeatNum}}`), 'g');
    const textSplitByDividers: string[] = [];

    textDividers.forEach((div) => {
        modifiedText = modifiedText.replace(new RegExp(div.pattern, 'g'), wrapUniqueSymbols(div.uniqueSymbol.repeat(repeatNum)));
    });

    while ((dividerStr = re.exec(modifiedText)) !== null) {
        if (currentIndex < dividerStr.index) {
            textSplitByDividers.push(modifiedText.slice(currentIndex, dividerStr.index));
        }

        const clearDivider = unwrapUniqueSymbols(dividerStr[0]);

        textSplitByDividers.push(clearDivider.slice(0, clearDivider.length / repeatNum));
        currentIndex = re.lastIndex;
    }

    if (currentIndex < modifiedText.length) {
        textSplitByDividers.push(modifiedText.slice(currentIndex));
    }

    return textSplitByDividers;
};

const transformLinksToConfigs = (text: string, params: TextParserParams): TextConfig[] => {
    const re = /<a[\w\W]+?<\/a>/g; // Ungreedy search
    const isLinkInStr: RegExpMatchArray | null = text.match(re);

    if (!isLinkInStr) {
        return [{ text }];
    }

    let linkStr;
    let currentIndex = 0;
    const result: TextConfig[] = [];

    while ((linkStr = re.exec(text)) !== null) {
        if (currentIndex < linkStr.index) {
            result.push({ text: text.slice(currentIndex, linkStr.index) });
        }

        result.push({ link: true, ...getDataFromLinkString(text.slice(linkStr.index, re.lastIndex), params) });
        currentIndex = re.lastIndex;
    }

    if (currentIndex < text.length) {
        result.push({ text: text.slice(currentIndex) });
    }

    return result;
};

const getDataFromLinkString = (linkStr: string, { internalLinkProps }: TextParserParams = {}): TextConfig => {
    const div: HTMLDivElement = document.createElement('div');
    div.innerHTML = linkStr;
    const { textContent, href, target } = div.firstChild as HTMLAnchorElement;

    return {
        text: textContent ?? '',
        linkProps: {
            ...({ href, target: target as LINK_TARGET }),
            ...internalLinkProps,
        },
    };
};

const transformTextToConfigs = (text: string | void, params: TextParserParams = {}): ParsedTextConfig[] => {
    if (!text) {
        return [];
    }

    return splitTextByDividers(text).reduce((res: ParsedTextConfig[], textItem: string) => {
        const divider = textDividers.find(divider => divider.uniqueSymbol === textItem) || null;

        if (divider) {
            res.push({ type: divider.symbol });
        } else {
            res.push(...transformLinksToConfigs(textItem, params));
        }

        return res;
    }, []);
};

export {
    textDividers,
    splitTextByDividers,
    transformLinksToConfigs,
    getDataFromLinkString,

    // main method
    transformTextToConfigs,
};
